/**
 * Validate a South African ID number and extract key properties.
 *
 * Accepts 13-digit strings. Strips spaces/dashes before validation.
 */
export function validateSaId(input) {
    const id = (input ?? '').replace(/[\s-]/g, '');
    if (!/^\d{13}$/.test(id)) {
        return {
            valid: false,
            idNumber: input,
            dateOfBirth: null,
            gender: null,
            age: null,
            citizenship: null,
            error: 'ID must be exactly 13 digits',
        };
    }
    // --- Date of birth ---
    const yy = parseInt(id.substring(0, 2), 10);
    const mm = parseInt(id.substring(2, 4), 10);
    const dd = parseInt(id.substring(4, 6), 10);
    if (mm < 1 || mm > 12) {
        return {
            valid: false,
            idNumber: input,
            dateOfBirth: null,
            gender: null,
            age: null,
            citizenship: null,
            error: `Invalid month: ${mm}`,
        };
    }
    // Century heuristic: YY <= current two-digit year → 20YY; else 19YY.
    const currentYear = new Date().getFullYear();
    const currentYY = currentYear % 100;
    const century = yy <= currentYY ? 2000 : 1900;
    let fullYear = century + yy;
    // Provisional date validation (handles leap years via Date overflow)
    let dob = new Date(fullYear, mm - 1, dd);
    if (dob.getFullYear() !== fullYear || dob.getMonth() !== mm - 1 || dob.getDate() !== dd) {
        return {
            valid: false,
            idNumber: input,
            dateOfBirth: null,
            gender: null,
            age: null,
            citizenship: null,
            error: `Invalid date: ${fullYear}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`,
        };
    }
    // If the provisional DOB is in the future relative to today,
    // the individual must have been born in the previous century.
    const today = new Date();
    const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (dob > todayNormalized) {
        fullYear -= 100;
        dob = new Date(fullYear, mm - 1, dd);
    }
    // --- Gender ---
    const genderDigit = parseInt(id.substring(6, 7), 10);
    const gender = genderDigit >= 5 ? 'Male' : 'Female';
    // --- Citizenship ---
    const citizenshipDigit = parseInt(id.substring(10, 11), 10);
    let citizenship;
    if (citizenshipDigit === 0) {
        citizenship = 'Citizen';
    }
    else if (citizenshipDigit === 1) {
        citizenship = 'Permanent Resident';
    }
    else if (citizenshipDigit === 2) {
        citizenship = 'Refugee';
    }
    else {
        return {
            valid: false,
            idNumber: input,
            dateOfBirth: null,
            gender: null,
            age: null,
            citizenship: null,
            error: `Invalid citizenship digit: ${citizenshipDigit}`,
        };
    }
    // --- Checksum (Luhn / Modulus 10) ---
    let sum = 0;
    for (let i = 0; i < 12; i++) {
        const d = parseInt(id[i], 10);
        if (i % 2 === 1) {
            const doubled = d * 2;
            sum += Math.floor(doubled / 10) + (doubled % 10);
        }
        else {
            sum += d;
        }
    }
    const expectedChecksum = (10 - (sum % 10)) % 10;
    const actualChecksum = parseInt(id[12], 10);
    if (expectedChecksum !== actualChecksum) {
        return {
            valid: false,
            idNumber: input,
            dateOfBirth: null,
            gender: null,
            age: null,
            citizenship: null,
            error: `Invalid checksum: expected ${expectedChecksum}, got ${actualChecksum}`,
        };
    }
    // --- Age ---
    let age = today.getFullYear() - fullYear;
    if (today.getMonth() < dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) {
        age--;
    }
    const dateOfBirth = `${fullYear}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    return {
        valid: true,
        idNumber: input,
        dateOfBirth,
        gender,
        age,
        citizenship,
    };
}
/**
 * Deterministically generates a valid South African ID number from an integer.
 *
 * - Guarantees zero collisions for any unique input in the range [0, 511,359,999].
 * - Distributes sequential inputs so that the resulting IDs look varied and organic.
 * - Always generates valid calendar dates between 1950-01-01 and 2019-12-31.
 *
 * @param seed An integer used to derive the ID properties.
 */
export function generateDeterministicTestId(seed) {
    // 1. Define the limits of the ID space to ensure we only generate valid properties.
    const totalDays = 25568; // Number of days between 1950-01-01 and 2019-12-31
    const totalSequences = 10000; // 0000 to 9999 (determines gender)
    const totalCitizenships = 2; // 0 (Citizen) or 1 (Permanent Resident)
    // Total unique valid combinations in this space: 511,360,000
    const maxDomain = totalDays * totalSequences * totalCitizenships;
    // 2. Normalize input and apply a prime-based multiplicative permutation.
    // This acts as a bijection on [0, maxDomain - 1] because 179424673 is coprime to maxDomain.
    const normalizedInput = Math.floor(Math.abs(seed));
    const scrambled = (normalizedInput * 179424673) % maxDomain;
    // 3. Bijectively decode the scrambled integer back into individual components.
    const citizenshipDigit = scrambled % totalCitizenships;
    const remainingValue = Math.floor(scrambled / totalCitizenships);
    const sequence = remainingValue % totalSequences;
    const dayOffset = Math.floor(remainingValue / totalSequences);
    // 4. Calculate the date of birth using UTC to prevent timezone shifts.
    const baseDate = new Date(Date.UTC(1950, 0, 1));
    const targetDate = new Date(baseDate.getTime() + dayOffset * 24 * 60 * 60 * 1000);
    const yy = String(targetDate.getUTCFullYear() % 100).padStart(2, '0');
    const mm = String(targetDate.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(targetDate.getUTCDate()).padStart(2, '0');
    // Format gender/sequence, citizenship, and the deprecated race digit (typically 8)
    const ssss = String(sequence).padStart(4, '0');
    const c = String(citizenshipDigit);
    const a = '8';
    const first12 = `${yy}${mm}${dd}${ssss}${c}${a}`;
    // 5. Compute the final Luhn checksum digit
    let sum = 0;
    for (let i = 0; i < 12; i++) {
        const d = parseInt(first12[i], 10);
        if (i % 2 === 1) {
            const doubled = d * 2;
            sum += Math.floor(doubled / 10) + (doubled % 10);
        }
        else {
            sum += d;
        }
    }
    const z = (10 - (sum % 10)) % 10;
    return `${first12}${z}`;
}
//# sourceMappingURL=sa-id.js.map